import type { TFunction } from 'i18next';

/**
 * ============================================================================
 * THE CODE → RUSSIAN TABLE. The server sends codes; this file owns the wording.
 * ============================================================================
 *
 * Every enumeration on the money wire crosses as its raw engine code —
 * `accounts.PortalAccount.Status` says so in its own comment («It is the CODE and not a
 * label: the portal renders the Russian wording, and a server-side label would be a second
 * translation of a closed set the SPA already has»), and so do the cycle state, the
 * suspension reason, the payment state/direction/source/correction kind, the promise state
 * and the charge type.
 *
 * Two properties make this file safe, and both are deliberate:
 *
 *  1. **An UNKNOWN code renders AS THE CODE, never as a blank and never as a guess.** The
 *     engine's vocabularies grow (a new payment source, a new suspension reason), and a
 *     portal that showed an empty «Состояние» column for a row it did not recognise would
 *     be hiding a fact from the subscriber, while one that guessed would be lying to him.
 *     `i18next`'s `defaultValue` is what makes that mechanical.
 *  2. **No Russian literal lives here.** Every string comes from `src/i18n/ru.ts` through
 *     `t`, which is what `src/strings.test.ts` enforces for the whole app — a code table is
 *     exactly the place a "temporary" inline label would survive for years.
 */

/** The `t` shape this module needs: a key and an options bag. */
type T = TFunction;

/** periodLabel turns YYYY-MM into «Октябрь 2022» — the month spinner's caption. */
export function periodLabel(period: string, t: T): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return period;
  return `${t(`money.month.${m[2]}`, { defaultValue: m[2] })} ${m[1]}`;
}

/** currencyLabel maps an ISO code to what the subscriber reads («руб», «$», «€»). */
export function currencyLabel(code: string, t: T): string {
  if (!code) return '';
  return t(`money.currency.${code}`, { defaultValue: code });
}

/**
 * accountStatusLabel is the Appendix C §2.2 code as a subscriber-facing state.
 *
 * ⚠ `awaiting_activation` OVERRIDES status 10, and that is the whole reason the flag is on
 * the wire. A brand-new Account is technically Disabled (10), which reads to a subscriber as
 * «отключено» — i.e. as something broken — when what it means is «press Activate». The
 * button itself is W4; the honest LABEL is this wave's.
 */
export function accountStatusLabel(status: number, awaitingActivation: boolean, t: T): string {
  if (awaitingActivation) return t('money.accountStatus.awaiting');
  switch (status) {
    case 0:
      return t('money.accountStatus.active');
    case 1:
    case 4:
      return t('money.accountStatus.financial');
    case 2:
      return t('money.accountStatus.userBlock');
    case 3:
      return t('money.accountStatus.adminBlock');
    case 5:
      return t('money.accountStatus.trafficBlock');
    case 10:
      return t('money.accountStatus.disabled');
    default:
      return String(status);
  }
}

/**
 * accountStatusTone decides the COLOUR of the status beside an account's login.
 *
 * ⚠ IT IS A SEPARATE FUNCTION FROM THE LABEL BECAUSE THE TWO GOT OUT OF STEP, and the
 * result was a screen that contradicted itself: until 2026-08-12 the tone was
 * `status === 1 || status === 4 ? bad : ok`, so an account reading «Заблокирована
 * оператором» (3), «Приостановлена по вашей заявке» (2), «Приостановлена по лимиту
 * трафика» (5) or «Отключена» (10) was painted GREEN — the colour this app uses for
 * «Активна». A subscriber who scans the colour before the word, which is what a colour is
 * for, read an interrupted service as a working one.
 *
 * Three tones and no more: `ok` is service running, `bad` is service stopped for a reason
 * he must act on, `warn` is service stopped for a reason that is not a fault. An UNKNOWN
 * code is `warn` and never `ok` — the engine's vocabularies grow, and painting a status
 * nobody has taught this file about as «everything is fine» is the defect above with a
 * later date on it.
 */
export function accountStatusTone(
  status: number,
  awaitingActivation: boolean,
): 'ok' | 'bad' | 'warn' {
  if (awaitingActivation) return 'warn';
  switch (status) {
    case 0:
      return 'ok';
    case 1:
    case 4:
    case 10:
      return 'bad';
    case 2:
    case 3:
    case 5:
      return 'warn';
    default:
      return 'warn';
  }
}

/** cycleStateLabel is the billing cycle's own state — "paid" / "unpaid" / "suspended". */
export function cycleStateLabel(state: string, t: T): string {
  if (!state) return '';
  return t(`money.cycleState.${state}`, { defaultValue: state });
}

/**
 * suspensionReasonLabel names WHY service stopped.
 *
 * The four codes are the engine's (`billing.Reason*`), and each means a different thing to
 * the subscriber: a failed subscription charge, a service charge he cannot cover, a promise
 * he did not repay, or a Model-A day. Collapsing them into «недостаточно средств» would
 * lose the one that is NOT about the subscription at all — `service_charge_failed`, where
 * the amount owed is a TV package's and not the tariff's.
 */
export function suspensionReasonLabel(reason: string, t: T): string {
  if (!reason) return t('money.reason.unknown');
  return t(`money.reason.${reason}`, { defaultValue: reason });
}

/**
 * floorLabel explains a resume figure that does not follow from «requirement» and «money».
 *
 * It is the field that lets the notice be honest about the two cases where the arithmetic a
 * subscriber would do in his head gives the wrong answer: a MISSED PROMISE asks for the
 * balance back inside the credit limit and charges nothing at all, and a WITHHELD period is
 * gated on SPENDABLE rather than availability, so it asks for the reservation too. Returns
 * an empty string for the ordinary case, where no explanation is owed.
 */
export function floorLabel(floor: string, t: T): string {
  if (!floor) return '';
  return t(`money.floor.${floor}`, { defaultValue: '' });
}

/**
 * paymentStateLabel is §22's «Состояние» — «Принят» for a posted or confirmed payment,
 * «Аннулирован» for a cancelled one.
 */
export function paymentStateLabel(state: string, t: T): string {
  switch (state) {
    case 'posted':
    case 'confirmed':
      return t('payments.state.accepted');
    case 'cancelled':
      return t('payments.state.cancelled');
    default:
      return state;
  }
}

/**
 * paymentSourceLabel is §22's «Способ платежа» — and it needs the DIRECTION.
 *
 * ⚠ A FUNDS TRANSFER WRITES TWO ROWS WITH THE SAME `source`. `payments.TransferFunds`
 * posts a DEBIT of −N on the source contract and a CREDIT of +N on the destination, both
 * `source = "transfer"`, and neither carries a `correction_kind` — so `correctionLabel`
 * below renders nothing for the debit, and the single wording «Перевод с другого договора»
 * was the only sentence on the row. Money LEAVING his contract was therefore described to
 * the subscriber as money arriving, on the one screen whose job is to account for it.
 *
 * The direction is the field that distinguishes them, exactly as it distinguishes a
 * reversal from a negative payment, so it is what this function reads.
 */
export function paymentSourceLabel(source: string, direction: string, t: T): string {
  if (!source) return '';
  if (source === 'transfer' && direction === 'debit') {
    return t('payments.source.transferOut');
  }
  return t(`payments.source.${source}`, { defaultValue: source });
}

/**
 * correctionLabel names the correction a row IS — the sentence that makes an append-only
 * ledger readable.
 *
 * A reversal with `correction_kind = "annul"` is «сторнирование: платёж аннулирован»; a
 * credit with `"restore"` is «платёж восстановлен». Without this the subscriber sees a
 * negative line with no explanation, which is the one thing §2.5's «visible through the
 * same cancellation/correction records» must not become.
 */
export function correctionLabel(p: { direction: string; correction_kind: string }, t: T): string {
  if (!p.correction_kind && p.direction !== 'reversal') return '';
  const kind = p.correction_kind || 'annul';
  const key = p.direction === 'reversal' ? 'reversalOf' : 'creditOf';
  return t(`payments.correction.${key}.${kind}`, {
    defaultValue: t(`payments.correction.${key}.other`),
  });
}

/** refundStateLabel is the money's journey back to the card (3–10 banking days, §2.5). */
export function refundStateLabel(state: string, t: T): string {
  if (!state || state === 'none') return '';
  return t(`payments.refund.${state}`, { defaultValue: state });
}

/** promiseStateLabel is §23's «Статус» — the ENGINE's four states, all first-class. */
export function promiseStateLabel(state: string, t: T): string {
  if (!state) return '';
  return t(`promises.state.${state}`, { defaultValue: state });
}

/** chargeTypeLabel groups §9's table: the tariff block and the services block. */
export function chargeTypeLabel(kind: string, t: T): string {
  if (!kind) return '';
  return t(`usage.chargeType.${kind}`, { defaultValue: kind });
}

/**
 * recommendedModeLabel says what the recommended payment MEANS, because §3.3's three
 * methods answer three different questions and the figure alone cannot be read.
 *
 * Mode 2 is the resume amount, and when it is the configured method the recommendation and
 * the suspension notice quote the SAME number — which is a fact worth naming rather than
 * leaving the subscriber to wonder whether he must pay both.
 */
export function recommendedModeLabel(mode: number, t: T): string {
  return t(`money.recommendedMode.${mode}`, { defaultValue: t('money.recommendedMode.other') });
}

/** billingModelLabel labels which of the three charge cadences an Account is on. */
export function billingModelLabel(model: string, t: T): string {
  if (!model) return '';
  return t(`money.billingModel.${model}`, { defaultValue: model });
}

/**
 * documentStateLabel is §19's «Состояние», and it reads BOTH fields the wire carries.
 *
 * ⚠ `payable` WAS ON THE WIRE AND IGNORED. The column keyed on `paid_at` alone, so an act
 * or a statement — a document that was never payable — rendered «Без оплаты», which is
 * exactly what an unpaid invoice rendered. A subscriber reading his own document list
 * could not tell a bill he owes from a record of service already rendered, and the
 * «Не оплаченные» filter selects both.
 *
 * Three states, because there are three facts: paid, owed, and nothing to pay.
 */
export function documentStateLabel(payable: boolean, paidAt: string | null, t: T): string {
  if (!payable) return t('documents.state.notPayable');
  return paidAt ? t('documents.state.paid') : t('documents.state.unpaid');
}

// ---- W3: the money-WRITE vocabularies ---------------------------------------------

/**
 * paymentsBlockedLabel says WHY the payment form is not offered, and the two reasons are
 * deliberately different sentences.
 *
 * ⚠ THE SERVER SPLITS THEM AND THE SCREEN MUST NOT MERGE THEM. `legal_entity` is a
 * decision the operator took about THIS subscriber (Appendix L §3 hides the payment
 * section for legal entities and sole proprietors unless he re-enables it);
 * `acquirer_unavailable` is a configuration state that affects everybody and will stop
 * being true when the merchant key is entered. Telling a company «попробуйте позже» is a
 * sentence that never comes true, and telling an individual «оплата вам недоступна»
 * because the operator has not finished configuring an acquirer is worse.
 */
export function paymentsBlockedLabel(reason: string, t: T): string {
  if (!reason) return '';
  return t(`topup.blocked.${reason}`, { defaultValue: t('topup.blocked.other') });
}

/** savedCardStatusLabel — S17's closed pair, «Активна» / «Истекла». */
export function savedCardStatusLabel(status: string, t: T): string {
  if (!status) return '';
  return t(`topup.savedCard.status.${status}`, { defaultValue: status });
}

/**
 * savedCardReasonLabel names WHY an anchor died, from the acquirer's own classifier slug.
 *
 * It exists so support can answer «почему перестал работать автоплатёж» without reading a
 * log — the wire's own stated reason for carrying the field. An unrecognised slug renders
 * AS THE SLUG, per this file's rule: the classifier's vocabulary grows at the acquirer,
 * and a blank line would hide from the subscriber the one string support will ask him for.
 */
export function savedCardReasonLabel(reason: string, t: T): string {
  if (!reason) return '';
  return t(`topup.savedCard.reason.${reason}`, { defaultValue: reason });
}

/** orderStateLabel — the engine's four order states (§8d step 3). */
export function orderStateLabel(state: string, t: T): string {
  if (!state) return '';
  return t(`topup.order.state.${state}`, { defaultValue: state });
}

/**
 * orderFailureLabel is §8b's THREE SENTENCES, chosen by the acquirer's own error code.
 *
 * ⚠ THE CODES ARE THE VENDOR'S «1/2/3» AND THE WORDING MIRRORS THE SERVER'S OWN
 * `payonline.failureMessage`, which renders the same three sentences on the acquirer's
 * return landing page. Two surfaces telling one subscriber two different things about one
 * declined payment is exactly the seam this project keeps finding, so the wording is kept
 * in step deliberately and the fallback is the same generic sentence.
 *
 * Anything else — `poll_declined`, an SBP status code — falls back to the acquirer's own
 * `failure_reason` when it sent one, because a sentence from the bank is worth more to
 * him than our paraphrase of a code we do not recognise.
 */
export function orderFailureLabel(code: string, reason: string, t: T): string {
  const known = t(`topup.order.failure.${code.trim()}`, { defaultValue: '' });
  if (known) return known;
  return reason || t('topup.order.failure.other');
}

/**
 * promiseBlockedLabel explains why a promised payment cannot be taken RIGHT NOW.
 *
 * The five codes are the CONTRACT domain's own, and `blocked_code` is literally the
 * refusal the grant would produce — one function decides both — so this panel cannot
 * offer what the service refuses nor refuse what it would allow. An unknown code falls
 * back to the generic sentence rather than to the raw code: unlike a status column, this
 * is a paragraph of prose, and a slug inside one reads as a fault rather than as an
 * answer. The button is hidden in either case, so nothing is hidden from him but a word.
 */
export function promiseBlockedLabel(code: string, t: T): string {
  if (!code) return '';
  return t(`promises.blocked.${code}`, { defaultValue: t('promises.blocked.other') });
}

/**
 * contractStatusLabel names a contract that is no longer live — «расторгнут».
 *
 * ⚠ IT RETURNS AN EMPTY STRING FOR AN ACTIVE CONTRACT, so the badge is absent rather than
 * reassuring. The wire carries `status` for exactly one reason: `ownedContract` admits a
 * TERMINATED contract (it refuses only archived and deleted ones), and until 2026-08-12
 * nothing in this SPA read the field — so a former customer opened the cabinet on a closed
 * contract, read its balance and its accounts, and nothing on the screen said so. It was
 * also the DEFAULT selection, because the picker falls back to the first row and no
 * contract in this database carries `default_in_personal_area`. D117 calls that «harmless
 * today and lethal the day the MySQL dumps land carrying real termination dates».
 */
export function contractStatusLabel(status: string, t: T): string {
  if (!status || status === 'active') return '';
  return t(`money.contractStatus.${status}`, { defaultValue: status });
}

// ---- W4: the ACCESS vocabularies (blockings, activation, devices, contacts) --------
//
// They live in this file rather than in one of their own for the reason its header gives:
// a code table is exactly the place a "temporary" inline label survives for years, and one
// table is what keeps two screens from coming to say different words about one code. The
// rule is unchanged — an UNKNOWN code renders AS THE CODE, never as a blank and never as a
// guess, because every one of these vocabularies grows at the engine.

/**
 * contactChannelLabel names the channel a confirmation code went out on.
 *
 * The server decides it from the KIND and reports it, so the screen states which inbox to
 * look in rather than inferring it. Two values today («sms», «email»); a third — a
 * messenger — is a channel the notification domain already has drivers for, so an
 * unrecognised value renders as itself rather than as «SMS».
 */
export function contactChannelLabel(channel: string, t: T): string {
  if (!channel) return '';
  return t(`profile.channel.${channel}`, { defaultValue: channel });
}

/**
 * scheduledTargetLabel says what a PENDING plan will DO — «приостановка» or
 * «возобновление» — rather than naming the status it will land in.
 *
 * ⚠ IT IS NOT `accountStatusLabel`. That function answers «what is the state now»
 * («Приостановлена по вашей заявке»), and a plan is a future EVENT: printing a state label
 * beside a date reads as though the account were already in it. Only the two targets this
 * surface can produce or cancel have wording; anything else renders as the code, which is
 * what a staff-created plan with some other target should look like — visible and
 * unexplained rather than mislabelled.
 */
export function scheduledTargetLabel(target: number, t: T): string {
  switch (target) {
    case 0:
      return t('blockings.target.resume');
    case 2:
      return t('blockings.target.block');
    default:
      return String(target);
  }
}

/**
 * deviceIdentityLabel names the STRONGEST identity form captured at registration.
 *
 * The engine's set is open (Amendment 02 §4.1 adds a form whenever a new qualifier can be
 * captured), so an unknown form renders as the raw slug: the subscriber can read it back to
 * support, which is worth more to him than our guess at what it means.
 */
export function deviceIdentityLabel(form: string, t: T): string {
  if (!form) return '';
  return t(`devices.identity.${form}`, { defaultValue: form });
}

/** deviceOriginLabel — who created the binding: the subscriber himself, or staff. */
export function deviceOriginLabel(origin: string, t: T): string {
  if (!origin) return '';
  return t(`devices.origin.${origin}`, { defaultValue: origin });
}

/**
 * leaseStateLabel names the state of the address a device holds.
 *
 * ⚠ IT IS ONLY EVER SHOWN BESIDE A KNOWN ADDRESS. `address_known: false` means the lease
 * read did not happen at all, and a state label there would be a fact invented about a
 * device we could not look at — see `PADevice.address_known`.
 */
export function leaseStateLabel(state: string, t: T): string {
  if (!state) return '';
  return t(`devices.lease.${state}`, { defaultValue: state });
}

/**
 * ticketOriginLabel — «Вы» or «Оператор»: who put the request into the system.
 *
 * §2.16 requires the distinction to be RECORDED, and it is worth showing: «оператор записал
 * мой звонок» and «я написал сам» are different memories, and a subscriber looking for the
 * ticket he raised last week should be able to tell them apart in a list.
 */
export function ticketOriginLabel(origin: string, t: T): string {
  if (!origin) return '';
  return t(`tickets.origin.${origin}`, { defaultValue: origin });
}

/**
 * ticketAuthorLabel names the author of one thread entry — a KIND and never a person.
 *
 * ⚠ THERE IS NO NAME ON THIS WIRE TO RENDER. §26b's own screenshot shows «Администратор», a
 * label rather than a person, and publishing a staff display name would put staff PII on an
 * internet-facing listener to answer a support ticket. So the portal owns three words, and
 * an unknown kind renders as itself rather than as «Оператор» — mislabelling an automatic
 * entry as a human reply is the one wrong guess available here.
 */
export function ticketAuthorLabel(kind: string, t: T): string {
  if (!kind) return '';
  return t(`tickets.author.${kind}`, { defaultValue: kind });
}

/**
 * notificationRowLabel names one row of §18's matrix.
 *
 * ⚠ THE ROW SET IS THE SERVER'S AND THIS TABLE MAY BE SHORTER THAN IT. The rows are derived
 * from the `notif_*` column list, so a fourteenth alert family appears on the portal the
 * moment its columns exist — and it must appear even before this file learns a Russian
 * sentence for it. An unknown key therefore renders as the KEY: visible, unexplained and
 * switchable, which is strictly better than a row the subscriber cannot see and cannot
 * control. (`AlertsScreen.test.tsx` renders an invented row for exactly this.)
 */
export function notificationRowLabel(key: string, t: T): string {
  if (!key) return '';
  return t(`alerts.row.${key}`, { defaultValue: key });
}

/** notificationChannelLabel — the column header «SMS» / «Email» / «Мессенджер». */
export function notificationChannelLabel(channel: string, t: T): string {
  if (!channel) return '';
  return t(`alerts.channel.${channel}`, { defaultValue: channel });
}

/**
 * notificationLockedLabel explains WHY a row cannot be changed here.
 *
 * The server sends a stable slug rather than a sentence («auto_generation_audience») so that
 * the portal words it and the vocabulary can be pinned. An unknown reason falls back to a
 * general sentence rather than to the slug: unlike a row key, this is prose in the middle of
 * a paragraph, and a bare slug there reads as a fault.
 */
export function notificationLockedLabel(reason: string, t: T): string {
  return t(`alerts.locked.${reason}`, { defaultValue: t('alerts.lockedGeneric') });
}

/**
 * tvStateLabel names one of the four 24TV states — a CLOSED vocabulary, derived on the
 * server, with one clear next action each.
 *
 * ⚠ `activation_needed` IS THE MONTHLY HOLE and its wording is the whole point of the
 * section: the subscriber is entitled, nothing was pushed for this month, and OPENING THE
 * 24TV APPLICATION is what fixes it. An unknown state renders as itself, because inventing
 * «всё в порядке» for a value this build has not met is the one answer that produces a
 * support call the section exists to prevent.
 */
export function tvStateLabel(state: string, t: T): string {
  if (!state) return '';
  return t(`tv.state.${state}`, { defaultValue: state });
}

/**
 * tvPacketLabel names a 24TV package — its NAME when the operator has fetched the
 * catalogue, and «Пакет №N» when he has not.
 *
 * ⚠ `name: null` MEANS «THE CATALOGUE WAS NEVER FETCHED», and it is not an empty name. A
 * blank there would tell a subscriber his package has no name; «null» would tell him the
 * portal is broken; the ID is a fact he can read back to support. It is the `address_known`
 * rule of the device wire applied to a label.
 */
export function tvPacketLabel(packet: { packet_id: number; name: string | null }, t: T): string {
  const name = (packet.name ?? '').trim();
  return name !== '' ? name : t('tv.packetById', { id: packet.packet_id });
}

// ---- W4 P3: tariff change, additional services, statistics -------------------------

/**
 * serviceStatusLabel is the Appendix C §2.5 per-service status as a subscriber reads it.
 *
 * Four codes today, and the two middle ones are the pair a screen must not merge:
 * `enabled_not_charged` is a service that is ON and inside a charge-free window, while
 * `enabled_charged` is one being paid for. An instance whose `valid_from` is in the future is
 * `not_enabled` — a plan he made, not a fault — and `disabled` is one that is over.
 *
 * An unknown code renders AS THE CODE, per this file's rule: the vocabulary grows at the
 * engine, and a blank «Состояние» cell hides a fact the subscriber can read back to support.
 */
export function serviceStatusLabel(status: string, t: T): string {
  if (!status) return '';
  return t(`services.status.${status}`, { defaultValue: status });
}

/**
 * statsTabLabel names one view of §14 — and there is only ONE, which is a measurement.
 *
 * ⚠ IT RETURNS AN EMPTY STRING FOR A TAB THIS BUILD CANNOT FILL, and that is what the caller
 * keys on: the three struck views («По часам», «По категориям», «По адресам») have no data
 * source in the ClickHouse migrations, so a heading for one would be a section with nothing
 * under it. The server's `tabs` array is the offer; this table is what the portal can honour,
 * and the intersection is what gets drawn.
 */
export function statsTabLabel(tab: string, t: T): string {
  if (tab !== 'days') return '';
  return t('statistics.tabDays');
}

/** tvStateTone — the badge colour for the four states. `activation_needed` is a WARNING. */
export function tvStateTone(state: string): 'ok' | 'warn' | 'bad' | 'badge' {
  switch (state) {
    case 'active':
      return 'ok';
    case 'activation_needed':
      return 'warn';
    default:
      // «unavailable» and «not_entitled» are not failures of his — they are facts about the
      // operator's partnership and about his tariff, so neither gets an alarming tint.
      return 'badge';
  }
}
